// Two languages, two objects, no framework.
//
// The legacy end-user application carried i18next and two JSON files for about
// a hundred and fifty strings. The administration interface is German only.
// The portal needs both — its users include people who do not read German —
// and this is what that costs: forty lines.
//
// The split with the server is deliberate. Anything the server *decides*
// arrives as a key and is translated here; anything the *customer wrote* —
// the name of an announcement, its text, a colleague's name — arrives as
// itself, because it exists only in the language they typed it in.

import { useSyncExternalStore } from "react";
// The legacy application's own translation files, verbatim. They are laid
// over the portal's keys below, so that every string the old screens showed is
// spelled exactly as it was; the portal's keys remain for what the server
// sends and for what is new.
import legacyDe from "./locales/de.json";
import legacyEn from "./locales/en.json";

const de = {
  // the way in
  "sign-in": "Anmelden",
  "references": "Referenzen",
  "contact": "Kontakt",
  "email": "E-Mail-Adresse",
  "password": "Passwort",
  "continue": "Weiter",
  "forgot-password": "Passwort vergessen?",
  "forgot-sent": "Wenn es zu dieser Adresse einen Zugang gibt, ist eine E-Mail unterwegs.",
  "set-password": "Passwort festlegen",
  "new-password": "Neues Passwort",
  "repeat-password": "Passwort wiederholen",
  "passwords-differ": "Die beiden Passwörter stimmen nicht überein.",
  "sign-in-failed": "Die Anmeldung ist fehlgeschlagen.",
  "link-expired": "Dieser Link ist nicht mehr gültig.",
  "password-set": "Ihr Passwort wurde gespeichert. Sie können sich jetzt anmelden.",
  "sign-out": "Abmelden",

  // the start page
  "voicemails": "Meine Favoriten",
  "favourites": "Favoriten",
  "planned": "Geplante Ansagen",
  "no-representation": "Keine Vertretung",
  "powered-by": "powered by gosh! Audio",
  "none-planned": "Keine Ansagen geplant",
  "new-announcement": "Neue Ansage",
  "add-favourite": "Favorit anlegen",
  "settings": "Einstellungen",

  // the form
  "which-announcement": "Welche Ansage?",
  "announcement-text": "Ansagetext",
  "title": "Bezeichnung (optional)",
  "start": "Abwesend ab",
  "end-inclusive": "Abwesend bis einschließlich",
  "reachable-from": "Erreichbar ab",
  "no-end": "Ohne Enddatum",
  "no-enddate": "Kein Enddatum",
  "date": "Datum",
  "time": "Uhrzeit",
  "start-time": "Startzeit",
  "end-time": "Endzeit",
  "accept": "Auswählen",
  "previous-month": "Voriger Monat",
  "next-month": "Nächster Monat",
  "duration": "Dauer",
  "indefinite": "Unbestimmt",
  "day": "Tag",
  "days": "Tage",
  "hour": "Stunde",
  "hours": "Stunden",
  "minute": "Minute",
  "minutes": "Minuten",
  "and": "und",
  "proxy": "Vertretung",
  "office-proxy": "Sekretariat",
  "please-choose": "Bitte wählen …",
  "field-required": "Diese Angabe ist erforderlich.",
  "no-recording-short": "ohne Aufnahme",
  "proxy-no-recording":
    "Für diese Vertretung ist noch keine Sprachaufnahme hinterlegt. Die Ansage kann damit nicht erzeugt werden — bitte wählen Sie eine andere oder wenden Sie sich an Ihre Administration.",
  "repeat": "Wiederholung",
  "notification": "Benachrichtigung",
  "listen": "Probe anhören",
  "save": "Speichern",
  "cancel": "Abbrechen",
  "delete": "Löschen",
  "end-now": "Jetzt beenden",
  "retry": "Erneut versuchen",

  // repeats
  "repeat-none": "Keine",
  "repeat-daily_no_weekend": "Täglich, ohne Wochenende",
  "repeat-daily": "Täglich",
  "repeat-weekly": "Wöchentlich",
  "repeat-monthly": "Monatlich",
  "repeat-yearly": "Jährlich",
  "repeat-needs-end":
    "Wiederholt werden kann nur eine Abwesenheit mit Enddatum.",
  "repeat-window-too-long":
    "Diese Abwesenheit dauert länger als ein Jahr und kann deshalb nicht wiederholt werden.",

  // notification
  "notify-none": "Niemanden",
  "notify-self": "Mich",
  "notify-self-and-proxy": "Mich und die Vertretung",

  // what an announcement is
  "date-announcement": "Datumsansage",
  "datetime-announcement": "Ansage mit Uhrzeit",
  "time-announcement": "Kurzansage",
  "without-enddate": "Ohne Enddatum",

  // status
  "planned-status": "Geplant",
  "activating": "Wird eingespielt",
  "active": "Aktiv",
  "deactivating": "Wird beendet",
  "failed": "Fehlgeschlagen",
  "retrying": "Wird wiederholt",

  // things that block
  "no-proxy-available": "Für diese Ansage ist keine Vertretung hinterlegt. Bitte wenden Sie sich an Ihre Administration.",
  "message-not-available": "Diese Ansage steht Ihnen nicht mehr zur Verfügung.",
  "no-announcement-types":
    "Für Ihre Firma sind noch keine Ansagen eingerichtet. Bitte wenden Sie sich an Ihre Administration.",
  "recording-missing": "Für Sie ist noch keine Namensaufnahme hinterlegt. Ihre Ansagen werden mit einer allgemeinen Stimme gesprochen.",
  "overlap": "Diese Abwesenheit überschneidet sich mit einer bereits geplanten:",

  // settings
  "personal-data": "Persönliche Daten",
  "name": "Name",
  "phone": "Telefon",
  "vpn-number": "VPN-Nummer",
  "department": "Abteilung",
  "contact-an-admin": "Änderungen nimmt Ihre Administration vor.",
  "default-proxy": "Standardvertretung",
  "virtual-proxy": "Office Vertretung",
  "no-proxy": "Keine",
  "search-name": "Name suchen …",
  "mailbox-pin": "Mobilbox-PIN",
  "mailbox-pin-set": "Ein PIN ist hinterlegt.",
  "mailbox-pin-missing": "Es ist kein PIN hinterlegt.",
  "change": "Ändern",
  "language": "Sprache",
  "german": "Deutsch",
  "english": "Englisch",
  "appearance": "Darstellung",
  "dark-mode": "Dunkler Modus",
  "administration": "Administration",
  "open-administration": "Zur Administration",
  "saved": "Gespeichert",
  "loading": "Wird geladen",
  "something-went-wrong": "Da ist etwas schiefgegangen.",

  // An administrator is using the portal in somebody else's name. The name and
  // the address are composed in the markup, because useT has no interpolation.
  "impersonation-title": "Stellvertretende Sitzung",
  "impersonation-by": "geöffnet von",
  "impersonation-note": "Alles, was hier gespeichert wird, wird im Namen dieser Person gespeichert.",
  "impersonation-end": "Sitzung beenden",
} as const;

export type Key = keyof typeof de;

// TypeScript fails the build when a key is missing here, which is the whole
// reason this is two objects rather than two JSON files.
const en: Record<Key, string> = {
  "sign-in": "Sign in",
  "references": "References",
  "contact": "Contact",
  "email": "E-mail address",
  "password": "Password",
  "continue": "Continue",
  "forgot-password": "Forgotten your password?",
  "forgot-sent": "If there is an account for this address, an e-mail is on its way.",
  "set-password": "Set a password",
  "new-password": "New password",
  "repeat-password": "Repeat the password",
  "passwords-differ": "The two passwords do not match.",
  "sign-in-failed": "Signing in failed.",
  "link-expired": "This link is no longer valid.",
  "password-set": "Your password has been saved. You can sign in now.",
  "sign-out": "Sign out",

  "voicemails": "My favourites",
  "favourites": "Favourites",
  "planned": "Planned announcements",
  "no-representation": "No stand-in",
  "powered-by": "powered by gosh! Audio",
  "none-planned": "Nothing planned",
  "new-announcement": "New announcement",
  "add-favourite": "Add a favourite",
  "settings": "Settings",

  "which-announcement": "Which announcement?",
  "announcement-text": "Announcement text",
  "title": "Label (optional)",
  "start": "Absent from",
  "end-inclusive": "Away until and including",
  "reachable-from": "Available from",
  "no-end": "No end date",
  "no-enddate": "No end date",
  "date": "Date",
  "time": "Time",
  "start-time": "Start time",
  "end-time": "End time",
  "accept": "Accept",
  "previous-month": "Previous month",
  "next-month": "Next month",
  "duration": "Duration",
  "indefinite": "indefinite",
  "day": "day",
  "days": "days",
  "hour": "hour",
  "hours": "hours",
  "minute": "minute",
  "minutes": "minutes",
  "and": "and",
  "proxy": "Stand-in",
  "office-proxy": "Office",
  "please-choose": "Please choose …",
  "field-required": "This is required.",
  "no-recording-short": "no recording",
  "proxy-no-recording":
    "There is no recording of this stand-in yet, so the announcement cannot be built. Please choose somebody else or ask your administration.",
  "repeat": "Repeat",
  "notification": "Notification",
  "listen": "Listen",
  "save": "Save",
  "cancel": "Cancel",
  "delete": "Delete",
  "end-now": "End now",
  "retry": "Try again",

  "repeat-none": "Never",
  "repeat-daily_no_weekend": "Daily, not at weekends",
  "repeat-daily": "Daily",
  "repeat-weekly": "Weekly",
  "repeat-monthly": "Monthly",
  "repeat-yearly": "Yearly",
  "repeat-needs-end": "Only an absence with an end date can repeat.",
  "repeat-window-too-long":
    "This absence lasts longer than a year, so it cannot repeat.",

  "notify-none": "Nobody",
  "notify-self": "Me",
  "notify-self-and-proxy": "Me and my stand-in",

  "date-announcement": "Date announcement",
  "datetime-announcement": "Announcement with a time",
  "time-announcement": "Short announcement",
  "without-enddate": "Without an end date",

  "planned-status": "Planned",
  "activating": "Being applied",
  "active": "Active",
  "deactivating": "Being ended",
  "failed": "Failed",
  "retrying": "Being retried",

  "no-proxy-available": "No stand-in is configured for this announcement. Please ask your administration.",
  "message-not-available": "This announcement is no longer available to you.",
  "no-announcement-types":
    "No announcements have been set up for your company yet. Please contact your administrator.",
  "recording-missing": "There is no recording of your name yet. Your announcements will be spoken in a generic voice.",
  "overlap": "This absence overlaps one that is already planned:",

  "personal-data": "Personal details",
  "name": "Name",
  "phone": "Phone",
  "vpn-number": "Short code",
  "department": "Department",
  "contact-an-admin": "Your administration makes these changes.",
  "default-proxy": "Default representation",
  "virtual-proxy": "Office representation",
  "no-proxy": "None",
  "search-name": "Search a name …",
  "mailbox-pin": "Mailbox PIN",
  "mailbox-pin-set": "A PIN is stored.",
  "mailbox-pin-missing": "No PIN is stored.",
  "change": "Change",
  "language": "Language",
  "german": "German",
  "english": "English",
  "appearance": "Appearance",
  "dark-mode": "Dark mode",
  "administration": "Administration",
  "open-administration": "Go to the administration",
  "saved": "Saved",
  "loading": "Loading",
  "something-went-wrong": "Something went wrong.",

  "impersonation-title": "Session on behalf of a user",
  "impersonation-by": "opened by",
  "impersonation-note": "Anything saved here is saved in this person's name.",
  "impersonation-end": "End session",
};

const dictionaries = {
  de: { ...de, ...legacyDe } as Record<string, string>,
  en: { ...en, ...legacyEn } as Record<string, string>,
};
export type Lang = keyof typeof dictionaries;

const STORAGE = "mbm.portal.lang";

function initial(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE);
    if (stored === "de" || stored === "en") return stored;
  } catch {
    // A private window refuses storage. German is the right guess for this
    // customer base.
  }
  return navigator.language?.toLowerCase().startsWith("en") ? "en" : "de";
}

let current: Lang = initial();
const listeners = new Set<() => void>();

export function setLanguage(lang: Lang) {
  current = lang;
  try {
    localStorage.setItem(STORAGE, lang);
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
  document.documentElement.lang = lang;
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

/** t translates a key the server sent, or one of our own. */
export function useT() {
  const lang = useLang();
  return (key: string, arg?: string | Record<string, string | number>): string => {
    const dict = dictionaries[lang];
    let out = dict[key] ?? (typeof arg === "string" ? arg : key);
    // i18next's {{x}}, which the legacy files use for counts.
    if (arg && typeof arg === "object") {
      for (const [k, v] of Object.entries(arg)) out = out.split(`{{${k}}}`).join(String(v));
    }
    return out;
  };
}
